/**
 * Centralized service for managing all consulting navigation and URL parameters
 * Handles browser history, URL updates, and presentation mode
 */

export type ViewedType = "USER" | "FAMILY";

export interface NavigationParams {
  id: number;
  type: ViewedType;
  preserveOtherParams?: boolean;
}

export interface ConsultingParams {
  id: number | null;
  type: ViewedType | null;
  presentation: boolean;
}

export class ConsultingNavigationService {
  private static readonly CONSULTING_PATH = "/consulting";

  /**
   * Updates URL and adds entry to browser history
   * @param params - Navigation parameters (id and type)
   * @param basePath - Base path for navigation (default: '/consulting')
   */
  static pushToHistory(params: NavigationParams, basePath: string = this.CONSULTING_PATH): void {
    const { id, type, preserveOtherParams = true } = params;

    // Get current URL params if we need to preserve them
    const currentParams = preserveOtherParams
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

    // Update or set the id and type
    currentParams.set("id", id.toString());
    currentParams.set("type", type);

    // Build the new URL
    const newUrl = `${basePath}?${currentParams.toString()}`;

    // Add to history (enables BACK button)
    window.history.pushState(null, "", newUrl);
  }

  /**
   * Gets all consulting navigation params from URL
   */
  static getConsultingParams(): ConsultingParams {
    const params = new URLSearchParams(window.location.search);
    const urlId = params.get("id");
    const urlType = params.get("type");
    const presentation = params.get("presentation") === "true";

    return {
      id: urlId ? parseInt(urlId) : null,
      type: urlType as ViewedType | null,
      presentation,
    };
  }

  /**
   * Gets current navigation params from URL (id and type only)
   */
  static getCurrentParams(): { id: number | null; type: ViewedType | null } {
    const params = new URLSearchParams(window.location.search);
    const urlId = params.get("id");
    const urlType = params.get("type");

    return {
      id: urlId ? parseInt(urlId) : null,
      type: urlType as ViewedType | null,
    };
  }

  /**
   * Checks if currently in presentation mode
   */
  static isPresentationMode(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.get("presentation") === "true";
  }

  /**
   * Enters presentation mode - adds presentation param to URL
   */
  static enterPresentationMode(id: number, type: ViewedType): void {
    const params = new URLSearchParams(window.location.search);
    params.set("presentation", "true");
    params.set("id", id.toString());
    params.set("type", type);

    const newUrl = `${this.CONSULTING_PATH}?${params.toString()}`;
    window.history.pushState(null, "", newUrl);
  }

  /**
   * Exits presentation mode - removes presentation param from URL
   */
  static exitPresentationMode(): void {
    const params = new URLSearchParams(window.location.search);
    params.delete("presentation");
    params.delete("id");
    params.delete("type");

    const newUrl = params.toString()
      ? `${this.CONSULTING_PATH}?${params.toString()}`
      : this.CONSULTING_PATH;

    window.history.pushState(null, "", newUrl);
  }

  /**
   * Checks if URL has valid navigation params
   */
  static hasValidParams(): boolean {
    const { id, type } = this.getCurrentParams();
    return id !== null && (type === "USER" || type === "FAMILY");
  }
}
